import type {
  JsonObject,
  JsonPrimitive,
  JsonValue,
  ModelAction,
  ModelObservationOutput,
  ModelSpec,
  ModelTransition,
  ObservationDataset,
} from '../../contracts/index.ts';
import type { ExpressionOperatorRegistry } from './ExpressionOperatorRegistry.ts';
import { z } from 'zod';
import {
  jsonObjectSchema,
  modelSpecSchema,
} from '../../contracts/index.ts';
import { ObservationStore } from '../ObservationStore.ts';
import { RuntimeClock } from '../RuntimeClock.ts';
import { BaseRuntimeSession } from '../RuntimeSession.ts';
import {
  createBuiltinExpressionOperatorRegistry,

} from './ExpressionOperatorRegistry.ts';
import { SafeExpressionEngine } from './SafeExpressionEngine.ts';

export const declarativeRuntimeConfigSchema = z.object({
  model: modelSpecSchema,
  parameters: jsonObjectSchema.default({}),
}).strict();

interface ScheduledModelEvent {
  id: number;
  time: number;
  type: string;
  payload: JsonObject;
}

type EntityCollections = Record<string, JsonObject[]>;

/**
 * Runtime inputs are JSON contracts, but Vue may wrap those values in reactive
 * proxies before they reach the runtime boundary. `structuredClone` rejects
 * proxies, so normalize through JSON here instead of leaking UI reactivity into
 * the deterministic runtime.
 */
function cloneRuntimeJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

interface ModelRuntimeStateSnapshot {
  elapsedTime: number;
  tick: number;
  generatedEntitySequence: number;
  scheduledEventSequence: number;
  randomState: number;
  modelState: Record<string, JsonPrimitive>;
  entities: EntityCollections;
  timerDue: Map<string, number>;
  scheduledEvents: ScheduledModelEvent[];
}

export class DeclarativeRuntimeValidationError extends Error {
  constructor(
    message: string,
    readonly path: string,
    readonly code = 'runtime.smoke_test_failed',
  ) {
    super(message);
    this.name = 'DeclarativeRuntimeValidationError';
  }
}

export class DeclarativeModelRuntime extends BaseRuntimeSession {
  private readonly model: ModelSpec;
  private readonly observations: ObservationStore;
  private readonly seed: number;
  private readonly operators: ExpressionOperatorRegistry;
  private readonly clock: RuntimeClock;
  private engine: SafeExpressionEngine;
  private modelState: Record<string, JsonPrimitive> = {};
  private entities: EntityCollections = {};
  private timerDue = new Map<string, number>();
  private scheduledEvents: ScheduledModelEvent[] = [];
  private tick = 0;
  private generatedEntitySequence = 0;
  private scheduledEventSequence = 0;
  private actionExecutionsThisStep = 0;

  constructor(input: {
    id: string;
    config: JsonObject;
    observations: ObservationStore;
    seed: number;
    operators?: ExpressionOperatorRegistry;
  }) {
    const config = declarativeRuntimeConfigSchema.parse(input.config);
    const parameters = DeclarativeModelRuntime.resolveParameters(
      config.model,
      config.parameters,
    );
    super(input.id, 'probfun.declarative-model@1', parameters);
    this.model = config.model;
    this.observations = input.observations;
    this.seed = input.seed;
    this.operators = input.operators
      ?? createBuiltinExpressionOperatorRegistry();
    DeclarativeModelRuntime.assertOperatorCollisions(
      this.model,
      this.operators,
    );
    this.engine = this.createExpressionEngine();
    this.clock = new RuntimeClock(
      () => this.step(1),
      500,
      undefined,
      () => this.nextPlaybackIntervalMs(),
    );
    this.validateExpressions();
    DeclarativeModelRuntime.assertEntityOutputFields(this.model);
    DeclarativeModelRuntime.assertDerivedGraph(this.model, this.engine);
    this.initializeModelState();
    this.publishAll();
  }

  protected onStart(): void {
    this.clock.start();
  }

  protected onPause(): void {
    this.clock.stop();
  }

  protected onResume(): void {
    this.clock.start();
  }

  protected onStop(): void {
    this.clock.stop();
  }

  protected onDispose(): void {
    this.clock.dispose();
  }

  protected onSpeedChange(): void {
    this.clock.setSpeed(this.speed);
  }

  protected onReset(): void {
    this.clock.stop();
    this.engine = this.createExpressionEngine();
    this.initializeModelState();
    for (const output of this.model.outputs)
      this.observations.remove(output.id);
    this.publishAll();
  }

  protected onParametersChange(parameters: JsonObject): void {
    DeclarativeModelRuntime.resolveParameters(this.model, parameters);
  }

  protected onParametersCommitted(): void {
    this.syncSnapshotState();
    this.publishAll();
  }

  protected onInteraction(eventType: string, payload: JsonObject): void {
    const before = this.captureModelState();
    this.actionExecutionsThisStep = 0;
    const scopePayload = cloneRuntimeJson(payload);
    try {
      for (const rule of this.model.interactions) {
        if (
          rule.eventType === eventType
          && this.evaluateCondition(rule.when, scopePayload)
        ) {
          this.applyActions(rule.actions, scopePayload);
        }
      }
      this.runTransitions('interaction', eventType, scopePayload);
      this.syncSnapshotState();
      this.publishAll(scopePayload);
    }
    catch (error) {
      this.restoreModelState(before);
      throw error;
    }
  }

  protected onStep(steps: number): void {
    for (let index = 0; index < steps; index += 1) {
      const before = this.captureModelState();
      this.actionExecutionsThisStep = 0;
      try {
        if (this.model.clock.mode === 'discrete-event')
          this.advanceDiscreteEvent();
        else
          this.advanceFixedStep();
      }
      catch (error) {
        this.restoreModelState(before);
        throw error;
      }
    }
  }

  private advanceFixedStep(): void {
    const delta = this.model.clock.timeStep;
    this.elapsedTime += delta;
    this.tick += 1;
    this.processDueTimers();
    this.processDueEvents();
    this.runTransitions('step');
    this.syncSnapshotState();
    this.publishAll();
  }

  private advanceDiscreteEvent(): void {
    const nextTimer = Math.min(...this.timerDue.values(), Infinity);
    const nextEvent = this.scheduledEvents[0]?.time ?? Infinity;
    const nextTime = Math.min(nextTimer, nextEvent);
    if (nextTime === Infinity) {
      this.elapsedTime += this.model.clock.timeStep;
      this.tick += 1;
      this.runTransitions('step');
    }
    else {
      this.elapsedTime = nextTime;
      this.tick += 1;
      this.processDueTimers();
      this.processDueEvents();
      this.runTransitions('step');
    }
    this.syncSnapshotState();
    this.publishAll();
  }

  private nextPlaybackIntervalMs(): number {
    if (this.model.clock.mode !== 'discrete-event')
      return this.model.clock.timeStep * 1000;
    const nextTimer = Math.min(...this.timerDue.values(), Infinity);
    const nextEvent = this.scheduledEvents[0]?.time ?? Infinity;
    const nextTime = Math.min(nextTimer, nextEvent);
    if (!Number.isFinite(nextTime))
      return this.model.clock.timeStep * 1000;
    return Math.max(0, nextTime - this.elapsedTime) * 1000;
  }

  private initializeModelState(): void {
    this.elapsedTime = 0;
    this.tick = 0;
    this.generatedEntitySequence = 0;
    this.scheduledEventSequence = 0;
    this.scheduledEvents = [];
    this.modelState = Object.fromEntries(
      this.model.state.map(variable => [variable.id, variable.initial]),
    );
    this.entities = Object.fromEntries(
      this.model.entities.map(collection => [
        collection.id,
        cloneRuntimeJson(collection.initial),
      ]),
    );
    for (const collection of this.model.entities) {
      if (
        collection.initial.length
        > this.model.limits.maxEntitiesPerCollection
      ) {
        throw new Error(`实体集合 ${collection.id} 的初始数量超过安全上限`);
      }
      this.assertUniqueEntityIds(collection.id);
    }
    this.timerDue.clear();
    for (const timer of this.model.timers) {
      const delay = this.numberResult(
        timer.initialDelay ?? timer.interval,
        this.scope(),
      );
      this.timerDue.set(timer.id, this.elapsedTime + Math.max(0, delay));
    }
    this.syncSnapshotState();
  }

  private processDueTimers(): void {
    for (const timer of this.model.timers) {
      let due = this.timerDue.get(timer.id);
      let executions = 0;
      const executionLimit = this.model.limits.maxTimerExecutionsPerStep;
      while (
        due !== undefined
        && due <= this.elapsedTime
        && executions < executionLimit
      ) {
        if (this.evaluateCondition(timer.when)) {
          this.applyActions(timer.actions);
          this.runTransitions('timer', timer.id);
        }
        executions += 1;
        if (!timer.repeat) {
          this.timerDue.delete(timer.id);
          break;
        }
        const interval = this.numberResult(timer.interval, this.scope());
        if (interval <= 0)
          throw new Error(`定时器 ${timer.id} 的间隔必须大于 0`);
        due += interval;
        this.timerDue.set(timer.id, due);
      }
      if (executions >= executionLimit)
        throw new Error(`定时器 ${timer.id} 在单步内触发次数过多`);
    }
  }

  private processDueEvents(): void {
    let executions = 0;
    while (
      this.scheduledEvents.length > 0
      && this.scheduledEvents[0]!.time <= this.elapsedTime
    ) {
      if (executions >= this.model.limits.maxActionsPerStep)
        throw new Error('单步内调度事件触发次数超过安全上限');
      const event = this.scheduledEvents.shift()!;
      this.runTransitions('event', event.type, event.payload);
      executions += 1;
    }
  }

  private runTransitions(
    triggerType: ModelTransition['trigger']['type'],
    triggerValue?: string,
    payload: JsonObject = {},
  ): void {
    for (const transition of this.model.transitions) {
      if (transition.trigger.type !== triggerType)
        continue;
      if (
        transition.trigger.type === 'timer'
        && transition.trigger.timerId !== triggerValue
      ) {
        continue;
      }
      if (
        (transition.trigger.type === 'event'
          || transition.trigger.type === 'interaction')
        && transition.trigger.eventType !== triggerValue
      ) {
        continue;
      }
      if (!this.evaluateCondition(transition.when, payload))
        continue;
      this.applyActions(transition.actions, payload);
    }
  }

  private applyActions(
    actions: ModelAction[],
    payload: JsonObject = {},
  ): void {
    for (const action of actions) {
      this.actionExecutionsThisStep += 1;
      if (
        this.actionExecutionsThisStep
        > this.model.limits.maxActionsPerStep
      ) {
        throw new Error('单步动作执行次数超过安全上限');
      }
      if (action.type === 'state.set') {
        const value = this.primitiveResult(
          action.value,
          this.scope(payload),
        );
        this.assertStateValue(action.target, value);
        this.modelState[action.target] = value;
      }
      else if (action.type === 'state.increment') {
        const current = this.modelState[action.target];
        if (typeof current !== 'number')
          throw new Error(`状态变量 ${action.target} 不是数值`);
        this.modelState[action.target] = current + this.numberResult(
          action.value,
          this.scope(payload),
        );
      }
      else if (action.type === 'entity.create') {
        const collection = this.requireCollection(action.collection);
        if (
          collection.length
          >= this.model.limits.maxEntitiesPerCollection
        ) {
          throw new Error(`实体集合 ${action.collection} 已达到安全上限`);
        }
        const values = this.evaluateFields(action.values, this.scope(payload));
        const idField = this.model.entities.find(
          item => item.id === action.collection,
        )?.idField ?? 'id';
        if (values[idField] === undefined) {
          do {
            this.generatedEntitySequence += 1;
            values[idField] = `${action.collection}-${this.generatedEntitySequence}`;
          } while (
            collection.some(entity =>
              String(entity[idField]) === String(values[idField]),
            )
          );
        }
        this.assertEntityIdAvailable(
          action.collection,
          String(values[idField]),
        );
        collection.push(values);
      }
      else if (action.type === 'entity.update') {
        const collection = this.requireCollection(action.collection);
        this.entities[action.collection] = collection.map((entity, index) => {
          const next = cloneRuntimeJson(entity);
          const baseScope = this.scope(payload, next, index);
          if (!this.evaluateCondition(action.when, payload, next, index))
            return next;
          for (const [field, expression] of Object.entries(action.set)) {
            next[field] = this.jsonResult(
              expression,
              { ...baseScope, entity: next },
            );
          }
          return next;
        });
        this.assertUniqueEntityIds(action.collection);
      }
      else if (action.type === 'entity.delete') {
        this.entities[action.collection] = this.requireCollection(
          action.collection,
        ).filter((entity, index) =>
          !this.evaluateCondition(action.when, payload, entity, index),
        );
      }
      else {
        const delay = this.numberResult(action.delay, this.scope(payload));
        if (delay < 0)
          throw new Error('调度事件的延迟不能小于 0');
        if (
          this.scheduledEvents.length
          >= this.model.limits.maxScheduledEvents
        ) {
          throw new Error('待处理事件数量已达到安全上限');
        }
        this.scheduledEventSequence += 1;
        this.scheduledEvents.push({
          id: this.scheduledEventSequence,
          time: this.elapsedTime + delay,
          type: action.eventType,
          payload: this.evaluateFields(action.payload, this.scope(payload)),
        });
        this.scheduledEvents.sort((left, right) =>
          left.time - right.time || left.id - right.id,
        );
      }
    }
  }

  private publishAll(payload: JsonObject = {}): void {
    const datasets = this.model.outputs.flatMap((output) => {
      const dataset = this.buildOutput(output, payload);
      return dataset ? [dataset] : [];
    });
    this.observations.applyBatch(datasets);
  }

  private buildOutput(
    output: ModelObservationOutput,
    payload: JsonObject,
  ): ObservationDataset | null {
    const rows: ObservationDataset['rows'] = [];
    const previous = this.observations.get(output.id);
    if (output.source === 'state') {
      if (this.evaluateCondition(output.when, payload)) {
        const scope = this.scope(payload);
        rows.push({
          id: output.rowId
            ? String(this.engine.evaluate(output.rowId, scope))
            : `${output.id}-${this.tick}`,
          values: this.evaluatePrimitiveFields(output.fields, scope),
        });
      }
      else if (previous) {
        return null;
      }
    }
    else {
      const collection = this.requireCollection(output.collection!);
      const idField = this.model.entities.find(
        item => item.id === output.collection,
      )?.idField ?? 'id';
      for (const [index, entity] of collection.entries()) {
        if (!this.evaluateCondition(output.when, payload, entity, index))
          continue;
        const scope = this.scope(payload, entity, index);
        rows.push({
          id: output.rowId
            ? String(this.engine.evaluate(output.rowId, scope))
            : String(entity[idField] ?? `${output.id}-${index}`),
          values: this.evaluatePrimitiveFields(output.fields, scope),
        });
      }
    }

    const maxRows = output.maxRows ?? this.model.limits.maxObservationRows;
    let updateMode = output.updateMode;
    let outputRows = rows;
    if (updateMode === 'append' && previous) {
      const combined = new Map(previous.rows.map(row => [row.id, row]));
      for (const row of rows)
        combined.set(row.id, row);
      if (combined.size > maxRows) {
        updateMode = 'replace';
        outputRows = [...combined.values()].slice(-maxRows);
      }
    }
    else if (outputRows.length > maxRows) {
      outputRows = outputRows.slice(-maxRows);
    }

    return {
      id: output.id,
      schemaVersion: '1.0.0',
      kind: output.kind,
      revision: (this.observations.getRevision(output.id) ?? -1) + 1,
      updateMode,
      dimensions: output.dimensions,
      rows: outputRows,
      metadata: {
        ...output.metadata,
        producerId: this.id,
        source: 'runtime',
        generatedAt: new Date().toISOString(),
        provenance: {
          runtime: this.capabilityKey,
          model: this.model.id,
        },
      },
    };
  }

  private scope(
    payload: JsonObject = {},
    entity?: JsonObject,
    index = -1,
  ): Record<string, unknown> {
    const scope: Record<string, unknown> = {
      ...cloneRuntimeJson(this.parameters),
      ...cloneRuntimeJson(this.modelState),
      ...cloneRuntimeJson(this.entities),
      state: cloneRuntimeJson(this.modelState),
      parameters: cloneRuntimeJson(this.parameters),
      time: this.elapsedTime,
      dt: this.model.clock.timeStep,
      tick: this.tick,
      payload: cloneRuntimeJson(payload),
      event: cloneRuntimeJson(payload),
      entity: entity ? cloneRuntimeJson(entity) : {},
      index,
    };
    const pending = [...this.model.derived];
    const errors = new Map<string, string>();
    while (pending.length > 0) {
      let progressed = false;
      for (let pendingIndex = pending.length - 1; pendingIndex >= 0; pendingIndex -= 1) {
        const derived = pending[pendingIndex]!;
        try {
          scope[derived.id] = this.engine.evaluate(derived.expression, scope);
          pending.splice(pendingIndex, 1);
          errors.delete(derived.id);
          progressed = true;
        }
        catch (error) {
          errors.set(
            derived.id,
            error instanceof Error ? error.message : '未知表达式错误',
          );
        }
      }
      if (!progressed) {
        const details = pending
          .map(item => `${item.id}: ${errors.get(item.id) ?? '无法解析'}`)
          .join('；');
        throw new Error(`派生值依赖无法解析（${details}）`);
      }
    }
    return scope;
  }

  private evaluateCondition(
    expression: string | undefined,
    payload: JsonObject = {},
    entity?: JsonObject,
    index = -1,
  ): boolean {
    if (!expression)
      return true;
    return Boolean(this.engine.evaluate(
      expression,
      this.scope(payload, entity, index),
    ));
  }

  private evaluateFields(
    fields: Record<string, string>,
    scope: Record<string, unknown>,
  ): JsonObject {
    return Object.fromEntries(
      Object.entries(fields).map(([field, expression]) => [
        field,
        this.jsonResult(expression, scope),
      ]),
    );
  }

  private evaluatePrimitiveFields(
    fields: Record<string, string>,
    scope: Record<string, unknown>,
  ): Record<string, JsonPrimitive> {
    return Object.fromEntries(
      Object.entries(fields).map(([field, expression]) => [
        field,
        this.primitiveResult(expression, scope),
      ]),
    );
  }

  private jsonResult(
    expression: string,
    scope: Record<string, unknown>,
  ): JsonValue {
    return this.engine.evaluate(expression, scope) as JsonValue;
  }

  private primitiveResult(
    expression: string,
    scope: Record<string, unknown>,
  ): JsonPrimitive {
    const value = this.engine.evaluate(expression, scope);
    if (
      value !== null
      && typeof value !== 'string'
      && typeof value !== 'number'
      && typeof value !== 'boolean'
    ) {
      throw new Error('观察字段必须得到基础 JSON 值');
    }
    return value;
  }

  private numberResult(
    expression: string,
    scope: Record<string, unknown>,
  ): number {
    const value = this.engine.evaluate(expression, scope);
    if (typeof value !== 'number' || !Number.isFinite(value))
      throw new Error(`表达式必须得到有限数值：${expression}`);
    return value;
  }

  private requireCollection(id: string): JsonObject[] {
    const collection = this.entities[id];
    if (!collection)
      throw new Error(`模型引用了未知实体集合：${id}`);
    return collection;
  }

  private assertStateValue(id: string, value: JsonPrimitive): void {
    const declaration = this.model.state.find(item => item.id === id);
    if (!declaration)
      throw new Error(`模型引用了未知状态变量：${id}`);
    const matches = (
      (declaration.type === 'number' && typeof value === 'number')
      || (declaration.type === 'boolean' && typeof value === 'boolean')
      || (declaration.type === 'string' && typeof value === 'string')
    );
    if (!matches)
      throw new Error(`状态变量 ${id} 的结果类型应为 ${declaration.type}`);
  }

  private assertEntityIdAvailable(
    collectionId: string,
    entityId: string,
  ): void {
    const declaration = this.model.entities.find(
      item => item.id === collectionId,
    );
    const idField = declaration?.idField ?? 'id';
    if (
      this.requireCollection(collectionId)
        .some(entity => String(entity[idField]) === entityId)
    ) {
      throw new Error(`实体集合 ${collectionId} 中编号重复：${entityId}`);
    }
  }

  private assertUniqueEntityIds(collectionId: string): void {
    const declaration = this.model.entities.find(
      item => item.id === collectionId,
    );
    const idField = declaration?.idField ?? 'id';
    const ids = new Set<string>();
    for (const entity of this.requireCollection(collectionId)) {
      if (entity[idField] === undefined)
        throw new Error(`实体集合 ${collectionId} 缺少编号字段 ${idField}`);
      const id = String(entity[idField]);
      if (ids.has(id))
        throw new Error(`实体集合 ${collectionId} 中编号重复：${id}`);
      ids.add(id);
    }
  }

  private captureModelState(): ModelRuntimeStateSnapshot {
    return {
      elapsedTime: this.elapsedTime,
      tick: this.tick,
      generatedEntitySequence: this.generatedEntitySequence,
      scheduledEventSequence: this.scheduledEventSequence,
      randomState: this.engine.getRandomState(),
      modelState: cloneRuntimeJson(this.modelState),
      entities: cloneRuntimeJson(this.entities),
      timerDue: new Map(this.timerDue),
      scheduledEvents: cloneRuntimeJson(this.scheduledEvents),
    };
  }

  private restoreModelState(snapshot: ModelRuntimeStateSnapshot): void {
    this.elapsedTime = snapshot.elapsedTime;
    this.tick = snapshot.tick;
    this.generatedEntitySequence = snapshot.generatedEntitySequence;
    this.scheduledEventSequence = snapshot.scheduledEventSequence;
    this.engine.restoreRandomState(snapshot.randomState);
    this.modelState = cloneRuntimeJson(snapshot.modelState);
    this.entities = cloneRuntimeJson(snapshot.entities);
    this.timerDue = new Map(snapshot.timerDue);
    this.scheduledEvents = cloneRuntimeJson(snapshot.scheduledEvents);
    this.syncSnapshotState();
  }

  private syncSnapshotState(): void {
    this.state = {
      ...cloneRuntimeJson(this.modelState),
      time: this.elapsedTime,
      tick: this.tick,
      entityCounts: Object.fromEntries(
        Object.entries(this.entities).map(([id, entities]) => [
          id,
          entities.length,
        ]),
      ),
    };
  }

  private createExpressionEngine(): SafeExpressionEngine {
    return new SafeExpressionEngine(
      this.seed,
      DeclarativeModelRuntime.allowedSymbols(this.model),
      this.operators,
    );
  }

  private validateExpressions(): void {
    for (const expression of DeclarativeModelRuntime.collectExpressions(this.model))
      this.engine.validate(expression);
  }

  static validateConfiguration(
    rawConfig: unknown,
    operators = createBuiltinExpressionOperatorRegistry(),
  ): void {
    const config = declarativeRuntimeConfigSchema.parse(rawConfig);
    const parameters = DeclarativeModelRuntime.resolveParameters(
      config.model,
      config.parameters,
    );
    const engine = new SafeExpressionEngine(
      1,
      DeclarativeModelRuntime.allowedSymbols(config.model),
      operators,
    );
    DeclarativeModelRuntime.assertOperatorCollisions(
      config.model,
      operators,
    );
    for (const expression of DeclarativeModelRuntime.collectExpressions(config.model))
      engine.validate(expression);
    DeclarativeModelRuntime.assertEntityOutputFields(config.model);
    DeclarativeModelRuntime.assertDerivedGraph(config.model, engine);
    DeclarativeModelRuntime.assertInitialDerivedValues(
      config.model,
      parameters,
      engine,
    );
    DeclarativeModelRuntime.runDeterministicSmokeTest(
      config as JsonObject,
      config.model,
      operators,
    );
  }

  private static runDeterministicSmokeTest(
    config: JsonObject,
    model: ModelSpec,
    operators: ExpressionOperatorRegistry,
  ): void {
    const observations = new ObservationStore();
    const runtime = new DeclarativeModelRuntime({
      id: '__validation__',
      config,
      observations,
      seed: 1,
      operators,
    });
    try {
      // A bounded, deterministic run exercises timer initialization, the
      // first event chains, state actions and ObservationDataset publication.
      // It deliberately does not start the wall-clock scheduler.
      runtime.onStep(24);
    }
    catch (error) {
      const message = error instanceof Error ? error.message : '运行探针失败';
      const timerId = message.match(/^定时器\s+(\S+)\s+的间隔必须大于 0$/)?.[1];
      const timerIndex = timerId
        ? model.timers.findIndex(timer => timer.id === timerId)
        : -1;
      throw new DeclarativeRuntimeValidationError(
        `Runtime 短程运行失败：${message}`,
        timerIndex >= 0
          ? `model.timers.${timerIndex}.interval`
          : 'model',
        timerIndex >= 0
          ? 'runtime.timer_interval_non_positive'
          : 'runtime.smoke_test_failed',
      );
    }
    finally {
      runtime.dispose();
    }
  }

  private static collectExpressions(model: ModelSpec): string[] {
    const expressions: string[] = [];
    for (const timer of model.timers) {
      expressions.push(timer.interval);
      if (timer.initialDelay)
        expressions.push(timer.initialDelay);
      if (timer.when)
        expressions.push(timer.when);
      expressions.push(...DeclarativeModelRuntime.actionExpressions(timer.actions));
    }
    for (const transition of model.transitions) {
      if (transition.when)
        expressions.push(transition.when);
      expressions.push(...DeclarativeModelRuntime.actionExpressions(transition.actions));
    }
    for (const rule of model.interactions) {
      if (rule.when)
        expressions.push(rule.when);
      expressions.push(...DeclarativeModelRuntime.actionExpressions(rule.actions));
    }
    for (const derived of model.derived)
      expressions.push(derived.expression);
    for (const output of model.outputs) {
      if (output.when)
        expressions.push(output.when);
      if (output.rowId)
        expressions.push(output.rowId);
      expressions.push(...Object.values(output.fields));
    }
    return expressions;
  }

  private static actionExpressions(actions: ModelAction[]): string[] {
    return actions.flatMap((action) => {
      if (action.type === 'state.set' || action.type === 'state.increment')
        return [action.value];
      if (action.type === 'entity.create')
        return Object.values(action.values);
      if (action.type === 'entity.update')
        return [...(action.when ? [action.when] : []), ...Object.values(action.set)];
      if (action.type === 'entity.delete')
        return [action.when];
      return [action.delay, ...Object.values(action.payload)];
    });
  }

  private static resolveParameters(
    model: ModelSpec,
    overrides: JsonObject,
  ): JsonObject {
    const known = new Set(model.parameters.map(parameter => parameter.id));
    for (const key of Object.keys(overrides)) {
      if (!known.has(key))
        throw new Error(`模型包含未声明参数：${key}`);
    }

    return Object.fromEntries(model.parameters.map((parameter) => {
      const value = overrides[parameter.id] ?? parameter.default;
      const matchesType = (
        (parameter.type === 'number' && typeof value === 'number')
        || (parameter.type === 'boolean' && typeof value === 'boolean')
        || (parameter.type === 'string' && typeof value === 'string')
      );
      if (!matchesType)
        throw new Error(`参数 ${parameter.id} 的类型应为 ${parameter.type}`);
      if (typeof value === 'number') {
        if (parameter.min !== undefined && value < parameter.min)
          throw new Error(`参数 ${parameter.id} 小于允许下限`);
        if (parameter.max !== undefined && value > parameter.max)
          throw new Error(`参数 ${parameter.id} 大于允许上限`);
      }
      return [parameter.id, value];
    }));
  }

  private static allowedSymbols(model: ModelSpec): Set<string> {
    return new Set([
      ...model.parameters.map(item => item.id),
      ...model.state.map(item => item.id),
      ...model.entities.map(item => item.id),
      ...model.derived.map(item => item.id),
      'state',
      'parameters',
      'time',
      'dt',
      'tick',
      'payload',
      'event',
      'entity',
      'index',
    ]);
  }

  private static assertDerivedGraph(
    model: ModelSpec,
    engine: SafeExpressionEngine,
  ): void {
    const derivedIds = new Set(model.derived.map(item => item.id));
    const dependencies = new Map(
      model.derived.map(item => [
        item.id,
        [...engine.referencedSymbols(item.expression)]
          .filter(symbol => derivedIds.has(symbol)),
      ]),
    );
    const visiting = new Set<string>();
    const visited = new Set<string>();
    const visit = (id: string): boolean => {
      if (visiting.has(id))
        return true;
      if (visited.has(id))
        return false;
      visiting.add(id);
      for (const dependency of dependencies.get(id) ?? []) {
        if (visit(dependency))
          return true;
      }
      visiting.delete(id);
      visited.add(id);
      return false;
    };
    if ([...derivedIds].some(visit))
      throw new Error('派生值依赖不能形成循环');
  }

  private static assertEntityOutputFields(model: ModelSpec): void {
    const fieldsByCollection = new Map(
      model.entities.map(collection => [
        collection.id,
        new Set([
          collection.idField ?? 'id',
          ...collection.initial.flatMap(entity => Object.keys(entity)),
        ]),
      ]),
    );
    const visitActions = (actions: ModelAction[]): void => {
      for (const action of actions) {
        if (action.type === 'entity.create') {
          const fields = fieldsByCollection.get(action.collection);
          for (const key of Object.keys(action.values))
            fields?.add(key);
        }
        else if (action.type === 'entity.update') {
          const fields = fieldsByCollection.get(action.collection);
          for (const key of Object.keys(action.set))
            fields?.add(key);
        }
      }
    };
    for (const timer of model.timers)
      visitActions(timer.actions);
    for (const transition of model.transitions)
      visitActions(transition.actions);
    for (const interaction of model.interactions)
      visitActions(interaction.actions);

    for (const output of model.outputs) {
      if (output.source !== 'entities' || !output.collection)
        continue;
      const available = fieldsByCollection.get(output.collection) ?? new Set();
      const expressions = [
        ...(output.when ? [output.when] : []),
        ...(output.rowId ? [output.rowId] : []),
        ...Object.values(output.fields),
      ];
      const referenced = new Set(expressions.flatMap(expression =>
        [...expression.matchAll(/\bentity\.([A-Za-z_]\w*)/g)]
          .map(match => match[1]!),
      ));
      const unknown = [...referenced].filter(field => !available.has(field));
      if (unknown.length > 0) {
        throw new Error(
          `实体输出 ${output.id} 引用了集合 ${output.collection} `
          + `未声明的字段：${unknown.sort().join('、')}`,
        );
      }
    }
  }

  private static assertInitialDerivedValues(
    model: ModelSpec,
    parameters: JsonObject,
    engine: SafeExpressionEngine,
  ): void {
    const clone = <T>(value: T): T =>
      JSON.parse(JSON.stringify(value)) as T;
    const state = Object.fromEntries(
      model.state.map(item => [item.id, item.initial]),
    );
    const entities = Object.fromEntries(
      model.entities.map(item => [item.id, clone(item.initial)]),
    );
    const scope: Record<string, unknown> = {
      ...clone(parameters),
      ...state,
      ...entities,
      state: clone(state),
      parameters: clone(parameters),
      time: 0,
      dt: model.clock.timeStep,
      tick: 0,
      payload: {},
      event: {},
      entity: {},
      index: -1,
    };
    const pending = [...model.derived];
    const errors = new Map<string, string>();
    while (pending.length > 0) {
      let progressed = false;
      for (let index = pending.length - 1; index >= 0; index -= 1) {
        const derived = pending[index]!;
        try {
          scope[derived.id] = engine.evaluate(derived.expression, scope);
          pending.splice(index, 1);
          errors.delete(derived.id);
          progressed = true;
        }
        catch (error) {
          errors.set(
            derived.id,
            error instanceof Error ? error.message : '无法计算',
          );
        }
      }
      if (!progressed) {
        const details = pending
          .map(item => `${item.id}: ${errors.get(item.id) ?? '无法计算'}`)
          .join('；');
        throw new Error(`派生值在初始状态不可计算（${details}）`);
      }
    }
  }

  private static assertOperatorCollisions(
    model: ModelSpec,
    operators: ExpressionOperatorRegistry,
  ): void {
    const names = [
      ...model.parameters.map(item => item.id),
      ...model.state.map(item => item.id),
      ...model.entities.map(item => item.id),
      ...model.derived.map(item => item.id),
    ];
    const collision = names.find(name => operators.has(name));
    if (collision)
      throw new Error(`模型标识符与表达式运算符冲突：${collision}`);
  }
}
